export type DeliveryAddressLike = {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  postalCode?: string | null;
  notes?: string | null;
} | null | undefined;

/** ФИО сотрудника для поставщика / чек-листов; никнейм — только запасной вариант. */
export function formatEmployeeName(
  user?: { fullName?: string | null; username?: string | null } | null,
  fallback = 'Сотрудник'
): string {
  const fullName = user?.fullName && String(user.fullName).trim();
  if (fullName) return fullName;
  const username = user?.username && String(user.username).trim();
  if (username) return username;
  return fallback;
}

/** Текст адреса доставки заказа (как в карточках заказчика). */
export function formatOrderDeliveryAddress(addr: DeliveryAddressLike): string {
  if (!addr) return '';
  const parts = [addr.addressLine1, addr.addressLine2, addr.city, addr.postalCode].filter(
    (p): p is string => !!p && String(p).trim().length > 0
  );
  const line = parts.join(', ');
  if (addr.notes?.trim()) {
    return line ? `${line}. Примечание: ${addr.notes.trim()}` : `Примечание: ${addr.notes.trim()}`;
  }
  return line;
}

type CustomerUserLike = {
  fullName?: string | null;
  username?: string | null;
  phone?: string | null;
  approval?: {
    responsibleFullName?: string | null;
    responsiblePhone?: string | null;
    responsiblePosition?: string | null;
    contactName?: string | null;
    contactPhone?: string | null;
  } | null;
} | null | undefined;

/** Ответственный с стороны заказчика: из заявки или учётной записи заказчика. */
export function getCustomerResponsibleContact(order: {
  customer?: { user?: CustomerUserLike } | null;
} | null): { fullName: string; phone: string; position: string } {
  const u = order?.customer?.user;
  const a = u?.approval;
  const fullName =
    (a?.responsibleFullName && String(a.responsibleFullName).trim()) ||
    (a?.contactName && String(a.contactName).trim()) ||
    (u?.fullName && String(u.fullName).trim()) ||
    (u?.username && String(u.username).trim()) ||
    '';
  const phone =
    (a?.responsiblePhone && String(a.responsiblePhone).trim()) ||
    (a?.contactPhone && String(a.contactPhone).trim()) ||
    (u?.phone && String(u.phone).trim()) ||
    '';
  const position = (a?.responsiblePosition && String(a.responsiblePosition).trim()) || '';
  return { fullName, phone, position };
}

const CANCEL_MIN_REASON_LEN = 5;
const CANCEL_MAX_REASON_LEN = 2000;

export { CANCEL_MIN_REASON_LEN, CANCEL_MAX_REASON_LEN };

/** Согласование отмены до смены статуса на cancelled / delivered. */
export function isOrderCancelNegotiationActive(order: {
  status?: string;
  cancelCustomerAgreed?: boolean;
  cancelSupplierAgreed?: boolean;
}): boolean {
  if (!order || order.status === 'cancelled' || order.status === 'delivered') return false;
  return !!(order.cancelCustomerAgreed || order.cancelSupplierAgreed);
}

/** Подпись в списке заказов: кто инициировал согласование отмены. */
export function getCancelNegotiationListLabel(order: {
  cancelCustomerAgreed?: boolean;
  cancelSupplierAgreed?: boolean;
}): string | null {
  if (order.cancelCustomerAgreed && !order.cancelSupplierAgreed) {
    return 'Запрос отмены от заказчика';
  }
  if (order.cancelSupplierAgreed && !order.cancelCustomerAgreed) {
    return 'Запрос отмены от поставщика';
  }
  return null;
}
